/**
 * @module app.core
 */

import { autoinject, observable, bindable, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { ApiQueryResponse } from 'app/shared/lib/essette-api';
import { ChunkedFileReader } from 'app/shared/lib/files';
import { apiCall } from 'app/shared/lib/api/api';
import { DocumentsService } from 'app/core/services/documents-service';
import { IDocumentsConfig } from 'app/core/models/essette-interfaces';
import { IApiUploadRequest, ApiError } from 'app/shared/lib/api/api-types';
import { getReference } from 'app/shared/models/essette-types';
import { Toaster } from 'app/shared/lib/toaster';
import { FaxService } from 'app/fm/services/fax-service';
import { deepClone } from 'app/shared/lib/utilities';
import { DocumentCategorySelectedEvent, DocumentFileUploadedEvent } from 'app/core/lib/events';
import { EventAggregator } from 'aurelia-event-aggregator';
import * as Core from 'app/core/models/essette-api';
import * as FM from 'app/fm/models/essette-api';

@autoinject
export class UploadDocument extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public documentsConfig: IDocumentsConfig = {};

    protected originalDocument: Core.Document = {};

    protected lockedCategoryId: string = '';

    @observable
    protected category: Core.DocumentCategory | null = null;

    protected categories: Core.DocumentCategory[] = [];

    protected description: string = '';
    protected summary: string = '';

    protected progress: number = -1;

    @observable
    protected file: File | null = null;

    protected maxFileSize = 40 * 1024 * 1024;

    protected uploadError: string = '';

    private uploadToken: string = '';
    private uploadName: string = '';

    protected isFaxDocument = false;

    ////****************** Element References ***************************************////



    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: DocumentsService,
        private readonly faxService: FaxService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.service.configure(this.documentsConfig);

        if(this.documentsConfig.categoryId) {
            this.lockedCategoryId = this.documentsConfig.categoryId;
        }

        if(this.documentsConfig.targetType == 'FM.FaxDocument') {
            this.isFaxDocument = true;
        }
        else {
            this.loadLookups();
        }
    }

    ////****************** Events ***************************************************////

    protected fileChanged(): void {
        if(this.file && this.file.size > this.maxFileSize) {
            this.uploadError = 'ui:messages.errors.upload.maximumSizeExceeded';
            return;
        }
        this.uploadFile();
    }

    ////****************** Functions ************************************************////

    public hasChanges(): boolean {
        return false;
    }

    public reset(): void {
        this.category = this.originalDocument.category || null;
        this.uploadToken = this.uploadName = '';
        this.description = this.originalDocument.description || '';
        this.summary = this.originalDocument.summary || '';
        this.file = null;
        this.progress = -1;
        this.uploadError = '';
    }

    public async submit(): Promise<boolean> {
        try {
            const document = this.buildDocument();
            if(!this.validate(document)) { return false; }

            if(!(this.documentsConfig && this.documentsConfig.addTargetProperty)) {
                throw new Error("Documents configuration must define addTargetProperty.")
            }

            if(this.documentsConfig.targetType == 'FM.FaxDocument') {
                const faxDocument = this.buildFaxDocument(document);
                this.documentsConfig.addTargetProperty(faxDocument);

                await this.saveFaxDocument(faxDocument);
            }
            else {
                this.documentsConfig.addTargetProperty(document);
                await this.saveDocument(document);
            }

            return true;
        }
        catch(error) {
            this.toaster.standard.saved('core:document.$', false);
            this.logger.error('Error Saving Document', error);
            return false;
        }
        finally {
            this.state.endSave();
        }
    }

    private async uploadFile(): Promise<void> {
        if(!this.file) { return; }

        const file = this.file;
        const reader = new ChunkedFileReader(file);
        try {
            this.state.beginUpload();
            this.uploadError = '';
            this.progress = -1;
            this.uploadToken = '';

            let request: IApiUploadRequest = {
                file: {
                    contentType: file.type || undefined,
                    name: file.name
                }
            };
            while(!reader.isComplete()) {
                const base64 = await reader.readBase64();

                request.contents = { data: [base64], encoding: 'base64' };
                const response = await this.service.uploadFile(request);
                if(!response.token || !response.token.value) {
                    break;
                }

                request.token = { value: response.token.value }
                delete request.file;

                this.progress = reader.calculateProgress();
            }

            if(request.token) {
                this.uploadToken = request.token.value;
                this.uploadName = file.name;
                this.events.publish(new DocumentFileUploadedEvent());
            }
        }
        catch(error) {
            let uploadError: string = 'ui:messages.errors.upload.$';
            if(error instanceof ApiError) {
                if(error.hasError('upload.unsupportedMediaType')) {
                    uploadError = 'ui:messages.errors.upload.unsupportedMediaType';
                }
            }
            this.uploadError = uploadError;
            this.progress = -1;
            this.uploadToken = '';

            throw error;
        }
        finally {
            this.state.endUpload();
        }
    }

    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();

            await this.loadCategories();

            this.originalDocument = this.buildDocument();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    protected tempValidateMessage(message: string): void {
        this.toaster.warn().title('Validation Failed').message(message).icon('ui:icons.validate').show();
    }

    protected validate(document: Core.Document): boolean {
        if(this.documentsConfig.targetType != 'FM.FaxDocument' && !document.category) {
            this.tempValidateMessage('Category is Required');
            return false;
        }
        // if(!document.upload.token)
        if(!this.uploadToken) {
            this.tempValidateMessage('File is Required');
            return false;
        }
        return true;
    }

    public buildDocument(): Core.Document {
        const document: Core.Document = <any>{
            category: getReference(this.category),
            description: this.description || undefined,
            summary: this.summary || undefined,
            file: { name: this.uploadName },
            upload: { token: { value: this.uploadToken } }
        };

        if(this.documentsConfig.taskId) {
            document.metadata = {
                created: {
                    byTask: { id: this.documentsConfig.taskId }
                }
            };
        }

        return document;
    }

    protected buildFaxDocument(document: Core.Document): FM.FaxDocument {
        const insertDocument = deepClone(document);
        insertDocument.category = { code: 'FaxRequest' };

        const faxDocument: FM.FaxDocument = <any>{
            document: insertDocument
        };

        return faxDocument;
    }

    protected handleCategorySelected() {
        this.events.publish(new DocumentCategorySelectedEvent(this.category));
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async saveDocument(document: Core.Document): Promise<void> {
        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.attachDocument(document);
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.saved('core:document.$', isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async saveFaxDocument(faxDocument: FM.FaxDocument): Promise<void> {
        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.faxService.attachFaxDocument(faxDocument);
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.saved('core:document.$', isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadCategories(): Promise<void> {
        const fields = ['description'];

        try {
            this.state.beginLoad();
            let response: ApiQueryResponse<Core.DocumentCategory>;
            if(this.lockedCategoryId) response = await this.service.loadCategoryById(this.lockedCategoryId, undefined, fields);
            else response = await this.service.loadCategories({ all: true }, undefined, fields);
            this.categories = response.results;

            // auto-select if only one category
            if(this.categories.length === 1) this.category = this.categories[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

}
